import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { capitalize } from "lodash";
import * as _ from "lodash";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import { InfoCardEnrichment, Popover, PopoverContent, PopoverTrigger, Type } from "@similarweb/ui-components";

import GAVerifiedContainer from "app/components/React/GAVerifiedIcon/GAVerifiedContainer";
import { UpgradeLink } from "app/components/React/Table/cells";
import {
    getFirstFromCommaSeparated,
    getLobKeyFromLobName,
    getVerticalFromLobName,
} from "app/components/React/Tooltip/LOBTooltip/utils";
import { SegmentTooltip } from "app/components/React/Tooltip/SegmentTooltip/SegmentTooltip";
import { i18nFilter } from "app/filters/i18nFilter";
import { AssetsService } from "app/services/AssetsService";
import { isLinesOfBusinessSegment, parseLinesOfBusinessSegmentId } from "app/services/segments/LinesOfBusinessUtils";
import { sitesOptions } from "app/services/sitesService/queries/sitesQueries";
import type { ILobResponse, SiteInfoResponse } from "app/services/sitesService/types";
import { SegmentTypeBadgeHorizontal } from "pro-features/components/core cells/src/CoreWebsiteCell/StyledComponents";

import BoxSubtitle from "../../../BoxSubtitle/src/BoxSubtitle";
import { IconType, SegmentIcon, StyledItemIcon } from "../CoreRecentCell/StyledComponents";

import {
    Container,
    ContentContainer,
    ExternalLink,
    GAIconContainer,
    InnerLinkContainer,
    Link,
    Subtitle,
    TextContainer,
} from "./StyledComponents";

const PopoverContentWrapper = styled(PopoverContent)`
    padding: 0;
    width: auto;
    max-width: none;
    background: transparent;
    box-shadow: none;
    border: none;
`;

export const CoreSegmentCell: any = ({
    segmentName,
    isOrgSegment,
    segmentId,
    lastModified,
    domain,
    onClick,
    icon,
    subtitleFilters,
    internalLink,
    externalLink,
    trackInternalLink = _.noop,
    trackExternalLink = _.noop,
    withWebsiteSign,
    target = "_self",
    className,
    showGaIcon,
    isGaPrivate,
    textAndIconMargin = "15px",
    domainBadge = null,
}: FixmeType) => {
    const isDisabled = domain === "grid.upgrade";
    const isLobSegment = typeof segmentId === "string" && isLinesOfBusinessSegment(segmentId);

    const lobSegmentData = useMemo(() => {
        if (!isLobSegment || !segmentId) {
            return null;
        }
        return parseLinesOfBusinessSegmentId(segmentId);
    }, [isLobSegment, segmentId]);

    const lobKey = useMemo(() => {
        if (!isLobSegment || !lobSegmentData?.businessLineName) {
            return null;
        }

        const cachedKey = getLobKeyFromLobName(lobSegmentData.businessLineName);
        return cachedKey || lobSegmentData.businessLineName;
    }, [isLobSegment, lobSegmentData]);

    const vertical = useMemo(() => {
        if (!isLobSegment || !lobSegmentData?.domain) {
            return null;
        }
        const siteForLookup = getFirstFromCommaSeparated(lobSegmentData.domain);
        return getVerticalFromLobName(siteForLookup) ?? null;
    }, [isLobSegment, lobSegmentData]);

    const { data: lobData, isLoading: isLoadingLobData } = useQuery({
        ...sitesOptions.siteLobInfo(
            {
                keys: lobSegmentData?.domain || "",
                lobKey: lobKey || "",
            },
            undefined,
            {
                enabled: isLobSegment && !!lobSegmentData?.domain && !!lobKey,
                staleTime: 60_000,
            },
        ),
        select: (responseData: SiteInfoResponse) => {
            const transformedData = responseData as ILobResponse;
            const icons = transformedData.DomainHeaders?.map((header) => header.Icon) ?? [];

            return {
                title: `Business Line • ${capitalize(vertical || "")}`,
                displayName: transformedData.DomainHeaders?.[0]?.Domain || lobSegmentData?.domain || "",
                description: transformedData.Description || "",
                image: transformedData.DomainHeaders?.[0]?.Image || "",
                icons: icons?.length === 1 ? icons[0] : icons,
                enrichmentCardName: transformedData.BusinessLineName || "",
            };
        },
    });

    let content;

    if (isDisabled) {
        content = (
            <InnerLinkContainer alignItems="center">
                <UpgradeLink />
            </InnerLinkContainer>
        );
    } else {
        const linkContent = (
            <Link
                target={target}
                href={internalLink}
                onClick={trackInternalLink}
            >
                <TextContainer marginLeft={textAndIconMargin}>{domain}</TextContainer>
            </Link>
        );

        content = (
            <InnerLinkContainer
                alignItems="center"
                hasDomainBadge={!!domainBadge}
            >
                {isLobSegment ? (
                    <Popover
                        isHover
                        placement="top"
                    >
                        <PopoverTrigger asChild>
                            <div>{linkContent}</div>
                        </PopoverTrigger>
                        <PopoverContentWrapper>
                            {lobData ? (
                                <InfoCardEnrichment
                                    isLoadingData={isLoadingLobData}
                                    // @ts-ignore FIXME: strict migration - Type 'string | (string | undefined)[] | undefined' is not assignable to type 'string | string[]' — DomainHeaders.Icon is optional in SiteInfoResponse, upstream type allows undefined entries
                                    icons={lobData.icons}
                                    imgSrc={lobData.image || AssetsService.assetUrl("/images/new-no-image.png")}
                                    description={
                                        lobData.description === ""
                                            ? i18nFilter()("infotip.description.undefined")
                                            : lobData.description
                                    }
                                    websiteName={lobData.displayName}
                                    title={lobData.title}
                                    enrichmentCardName={lobData.enrichmentCardName}
                                />
                            ) : null}
                        </PopoverContentWrapper>
                    </Popover>
                ) : (
                    <SegmentTooltip
                        domain={domain}
                        segmentName={segmentName}
                        segmentId={segmentId}
                        isOrgSegment={isOrgSegment}
                        dateModified={lastModified}
                        onClick={onClick}
                    >
                        {linkContent}
                    </SegmentTooltip>
                )}
                {showGaIcon ? (
                    <GAIconContainer>
                        <GAVerifiedContainer
                            isActive={true}
                            isPrivate={isGaPrivate}
                            metric="visits"
                            tooltipIsActive={false}
                            tooltipAvailable={true}
                            size="SMALL"
                        />
                    </GAIconContainer>
                ) : null}
                {externalLink ? (
                    <ExternalLink
                        className={"link-out"}
                        href={externalLink}
                        target="_blank"
                        onClick={trackExternalLink}
                    >
                        <SWReactIcons iconName="link-out" />
                    </ExternalLink>
                ) : null}
            </InnerLinkContainer>
        );
    }

    return (
        <Container className={className}>
            {!isDisabled ? (
                <>
                    <StyledItemIcon
                        iconType={Type.Website}
                        iconSrc={icon}
                    />
                    {withWebsiteSign ? (
                        <IconType>
                            <SegmentIcon iconName="chart-pie" />
                        </IconType>
                    ) : null}
                </>
            ) : null}

            <ContentContainer hasBadge={domainBadge}>
                {content}
                {subtitleFilters ? (
                    <Subtitle>
                        <BoxSubtitle filters={subtitleFilters} />
                    </Subtitle>
                ) : null}
            </ContentContainer>
            {domainBadge ? (
                <SegmentTypeBadgeHorizontal className="core-website-cell--domain-badge">
                    {domainBadge}
                </SegmentTypeBadgeHorizontal>
            ) : null}
        </Container>
    );
};
